import { ChronoUnit, Duration, Instant, LocalDate, LocalDateTime, ZoneId } from "@js-joda/core";
import { IUserSessionData } from "api/model/user/IUserSessionData";
import { SelectItem } from "primereact/selectitem";
import { fmtDatetime } from "utils/time-utils";
import { writeFile, utils as XLSXUtils } from "xlsx";

export interface IUserStatisticsComponentProps {
  userSessionData: IUserSessionData[];
  groupBy: GroupingType;
  companyFilter: string | undefined;
  jobTitleFilter: string | undefined;
}

interface CommonProcessedStatsData {
  totalSessions: number;
  totalSessionTime: number;
  activeDaysCount?: number;
  averageSessionDuration?: number;
}

export type GroupingType = "username" | "jobTitle";

export interface ProcessedUserData extends CommonProcessedStatsData {
  user: string;
  activeDaysList: Set<string>;
  jobTitle?: string;
}

export interface ProcessedJobData extends CommonProcessedStatsData {
  jobTitle: string;
  activeDaysList: string[];
}

export function preProcessUserData(userSessionData: IUserSessionData[], groupBy: GroupingType) {
  return (
    userSessionData?.reduce((groups: ProcessedUserData[], user) => {
      const existingGroup = groups.find(g => {
        if (groupBy === "username") {
          return g.user === user.username;
        }
        return g.jobTitle === user.jobTitle;
      });
      let group = existingGroup;
      if (!group) {
        group = {
          user: user.username,
          jobTitle: user.jobTitle,
          activeDaysList: new Set<string>(),
          totalSessions: 0,
          totalSessionTime: 0,
        };
      }

      if (user.from && user.to) {
        const tempFrom = Instant.parse(user.from);
        const tempTo = Instant.parse(user.to);
        const sessionDuration = Duration.between(tempFrom, tempTo).seconds();

        group.totalSessions += 1;
        group.totalSessionTime += sessionDuration;
        const loginDateFrom = LocalDateTime.ofInstant(tempFrom).toLocalDate().toString();
        group.activeDaysList.add(loginDateFrom);
        group.activeDaysCount = group.activeDaysList.size;
        group.averageSessionDuration = group.totalSessionTime / group.totalSessions;
      }

      if (!existingGroup) {
        groups.push(group);
      } else {
        groups.map(g => {
          if (groupBy === "username") {
            return g.user === group.user ? group : g;
          }
          return g.jobTitle === group.jobTitle ? group : g;
        });
      }

      return groups;
    }, []) ?? []
  );
}

export const exportUserSessions = (users: IUserSessionData[], userOptions: SelectItem[], tz: ZoneId) => {
  const fileData = users.map(user => ({
    user: userOptions.find(u => u.value === user.username)?.label || user.username,
    username: user.username,
    start: fmtDatetime(user.from, tz),
    end: user.to && fmtDatetime(user.to, tz),
    "duration, mins":
      user.to && Duration.between(Instant.parse(user.from), Instant.parse(user.to)).toMinutes(),
  }));

  const worksheet = XLSXUtils.json_to_sheet(fileData, {});

  if (fileData.length > 0) {
    const headers = Object.keys(fileData[0]);
    const widthMap: Record<string, number> = {
      user: 22,
      username: 28,
      start: 16,
      end: 16,
      "duration, mins": 14,
    };
    const colWidths = headers.map(h => ({ wch: widthMap[h] ?? 20 }));
    worksheet["!cols"] = colWidths;
  }

  const workbook = XLSXUtils.book_new();
  XLSXUtils.book_append_sheet(workbook, worksheet, "sessions");
  writeFile(workbook, "user-sessions.xlsx");
};

export function getFirstDayOfLastMonth(): Date {
  const today = LocalDate.now();
  const lastMonth = today.minus(1, ChronoUnit.MONTHS).withDayOfMonth(1);
  return new Date(lastMonth.year(), lastMonth.monthValue() - 1, lastMonth.dayOfMonth());
}

export function getStartDate(daysAgo: number): Date {
  const today = LocalDate.now();
  const startDate = today.minus(daysAgo, ChronoUnit.DAYS);
  return new Date(startDate.year(), startDate.monthValue() - 1, startDate.dayOfMonth());
}

export function secondsToHm(d: number) {
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);

  const hDisplay = h > 0 ? `${h}h ` : "";
  const mDisplay = m > 0 ? `${m}m ` : "";

  return hDisplay + mDisplay;
}
